import { core } from '../core/index.js';
import { filterFabricErrors } from '../errors/errors.js';
import {
  type DrivingRouteSearchParam,
  type DrivingRouteSearchResult,
} from './map-search-api/driving-route-search.js';
import {
  type DistanceSearchParam,
  type DistanceSearchResult,
  type FabricSearchMapMethodType,
  type PoiKeywordsSearchParam,
  type PoiKeywordsSearchResult,
  type ReGeocodeSearchParam,
  type ReGeocodeSearchResult,
} from './map-search-api/index.js';
import {
  type PoiAroundSearchParam,
  type PoiAroundSearchResult,
} from './map-search-api/poi-around-search.js';

export type FabricSearchMapOptions<T extends FabricSearchMapMethodType> = {
  /**
   * 搜索方法
   */
  method: T;
  /**
   * 搜索参数
   */
  param: T extends 'poiKeywordsSearch'
    ? PoiKeywordsSearchParam
    : T extends 'poiAroundSearch'
      ? PoiAroundSearchParam
      : T extends 'reGeocodeSearch'
        ? ReGeocodeSearchParam
        : T extends 'distanceSearch'
          ? DistanceSearchParam
          : T extends 'drivingRouteSearch'
            ? DrivingRouteSearchParam
            : never;
};

export type FabricSearchMapResult<T extends FabricSearchMapMethodType> =
  T extends 'poiKeywordsSearch'
    ? PoiKeywordsSearchResult
    : T extends 'poiAroundSearch'
      ? PoiAroundSearchResult
      : T extends 'reGeocodeSearch'
        ? ReGeocodeSearchResult
        : T extends 'distanceSearch'
          ? DistanceSearchResult
          : T extends 'drivingRouteSearch'
            ? DrivingRouteSearchResult
            : never;

export type FabricSearchMapApi = <T extends FabricSearchMapMethodType>(
  options: FabricSearchMapOptions<T>
) => Promise<FabricSearchMapResult<T>>;

/**
 * 打车地图直接搜索位置.
 * @example
 * ```ts
 * void mapSearch({
 *   method: 'poiKeywordsSearch',
 *   param: {
 *     keywords: '',
 *   },
 * }).then((result) => {
 *   console.log(result.count);
 * });
 * ```
 */
export const searchMap = <T extends FabricSearchMapMethodType>(
  options: FabricSearchMapOptions<T>
): Promise<FabricSearchMapResult<T>> => {
  return new Promise<FabricSearchMapResult<T>>((resolve, reject) => {
    core.call<
      'fabric_map.searchMap',
      FabricSearchMapOptions<T>,
      FabricSearchMapResult<T>
    >('fabric_map.searchMap', options, (result) => {
      const fabricErrors = filterFabricErrors(
        result.code,
        result.message,
        result.data
      );
      if (fabricErrors) {
        reject(fabricErrors);
      } else {
        resolve(result.data);
      }
    });
  });
};
